import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core'
import { HttpClient } from '@angular/common/http'
import { Subject, Subscription, of } from 'rxjs'
import { catchError, debounceTime, distinctUntilChanged, finalize, switchMap, tap } from 'rxjs/operators'
import { ToastrService } from 'ngx-toastr'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
import { UserReviewClient } from 'src/app/shared/services/ppv.api'
import { ReviewTemplate } from '../review-assessment-sections/review-assessment-sections.component'
import {
   PERFORMANCE_OPTIONS,
   PeerCandidate,
   PeerInputDetail,
   PeerInputRequest,
   PeerInputResponse,
   apiBase,
   formatAnswerText,
   initials,
   mapAdditionalResponsibilities,
   plainLabel,
   readReviewerAnswer,
} from '../../peer-input'

dayjs.extend(utc)

interface CopyItem {
   questionId: string
   label: string
   theirs: string
   yours: string
   response: PeerInputResponse
   selected: boolean
}

interface CopyGroup {
   title: string
   items: CopyItem[]
}

/**
 * "Peer Input" card on the review page. The manager searches for a colleague
 * (same hospital, or same role at another hospital), sends them a blank copy of
 * the manager's side of this review, and later copies any of their answers in.
 */
@Component({
   selector: 'peer-input-panel',
   templateUrl: './peer-input-panel.component.html',
   styleUrls: ['./peer-input-panel.component.scss'],
   standalone: false,
})
export class PeerInputPanelComponent implements OnInit, OnChanges, OnDestroy {
   @Input() revieweeUserId: number
   @Input() reviewType: string
   @Input() reviewId: number | null = null
   @Input() practiceSKey: number | null = null
   @Input() practiceName = ''
   @Input() revieweeName = ''
   @Input() template: ReviewTemplate
   /** The manager's review, used to show "Yours" next to each peer answer. */
   @Input() userReview: any
   @Output() copyAnswers = new EventEmitter<PeerInputResponse[]>()

   requests: PeerInputRequest[] = []
   loading = false

   // Search
   showSearch = false
   searchText = ''
   searching = false
   candidates: PeerCandidate[] = []
   sendingTo: number | null = null
   private search$ = new Subject<string>()
   private searchSub?: Subscription

   // Copy dialog
   activeRequest: PeerInputRequest | null = null
   copyGroups: CopyGroup[] = []
   loadingDetail = false

   initials = initials

   constructor(
      private http: HttpClient,
      private toastr: ToastrService,
      private userReviewClient: UserReviewClient
   ) {}

   private get base(): string {
      return apiBase(this.userReviewClient)
   }

   ngOnInit(): void {
      this.searchSub = this.search$
         .pipe(
            debounceTime(300),
            distinctUntilChanged(),
            tap((q) => (this.searching = q.trim().length >= 2)),
            switchMap((q) => {
               if (q.trim().length < 2) return of([] as PeerCandidate[])
               const params: any = { revieweeUserId: this.revieweeUserId, q: q.trim() }
               if (this.practiceSKey) params.practiceSKey = this.practiceSKey
               return this.http
                  .get<PeerCandidate[]>(`${this.base}/api/userreview/peer-input/candidates`, { params })
                  .pipe(
                     catchError(() => of([] as PeerCandidate[])),
                     finalize(() => (this.searching = false))
                  )
            })
         )
         .subscribe((results) => {
            this.candidates = results ?? []
            this.searching = false
         })
   }

   ngOnChanges(changes: SimpleChanges): void {
      if ((changes['revieweeUserId'] || changes['reviewType'] || changes['reviewId']) && this.revieweeUserId && this.reviewType) {
         this.loadRequests()
      }
   }

   ngOnDestroy(): void {
      this.searchSub?.unsubscribe()
   }

   // ── List ──────────────────────────────────────────────────────────────────

   loadRequests(): void {
      const params: any = { revieweeUserId: this.revieweeUserId, reviewType: this.reviewType }
      if (this.reviewId) params.reviewId = this.reviewId

      this.loading = true
      this.http.get<PeerInputRequest[]>(`${this.base}/api/userreview/peer-input`, { params }).subscribe({
         next: (rows) => {
            this.requests = rows ?? []
            this.loading = false
         },
         error: () => (this.loading = false),
      })
   }

   get submittedCount(): number {
      return this.requests.filter((r) => r.status === 'Submitted').length
   }

   shortDate(value: string | null): string {
      return value ? dayjs.utc(value).local().format('MMM D') : ''
   }

   copyLink(r: PeerInputRequest): void {
      navigator.clipboard?.writeText(r.link).then(
         () => this.toastr.success(`Link copied. You can paste it into a message to ${r.peerName}.`),
         () => this.toastr.error('Could not copy the link.')
      )
   }

   remove(r: PeerInputRequest): void {
      if (!confirm(`Cancel the request to ${r.peerName}? Their link will stop working.`)) return

      this.http.delete(`${this.base}/api/userreview/peer-input/${r.id}`).subscribe({
         next: () => {
            this.requests = this.requests.filter((x) => x.id !== r.id)
            this.toastr.success('Request cancelled')
         },
         error: (err) => this.toastr.error(this.errorText(err, 'Could not cancel this request.')),
      })
   }

   // ── Search & send ─────────────────────────────────────────────────────────

   toggleSearch(): void {
      this.showSearch = !this.showSearch
      if (!this.showSearch) this.resetSearch()
   }

   onSearchChange(value: string): void {
      this.search$.next(value ?? '')
   }

   send(c: PeerCandidate): void {
      if (this.sendingTo !== null) return
      this.sendingTo = c.userId

      this.http
         .post<PeerInputRequest>(`${this.base}/api/userreview/peer-input`, {
            revieweeUserId: Number(this.revieweeUserId),
            reviewType: this.reviewType,
            reviewId: this.reviewId,
            practiceSKey: this.practiceSKey,
            practiceName: this.practiceName,
            revieweeName: this.revieweeName,
            peerUserId: c.userId,
         })
         .subscribe({
            next: (row) => {
               this.sendingTo = null
               this.requests = [row, ...this.requests.filter((x) => x.id !== row.id)]

               if (row.alreadyRequested && row.status === 'Submitted') {
                  this.toastr.info(`${row.peerName} already sent their input for this review.`)
               } else if (row.alreadyRequested) {
                  this.toastr.success(`${row.peerName} was already asked. We sent them the link again.`)
               } else {
                  this.toastr.success(`Request sent. ${row.peerName} will get an email with a link.`)
               }

               this.showSearch = false
               this.resetSearch()
            },
            error: (err) => {
               this.sendingTo = null
               this.toastr.error(this.errorText(err, 'Could not send the request. Please try again.'))
            },
         })
   }

   private resetSearch(): void {
      this.searchText = ''
      this.candidates = []
      this.searching = false
      this.search$.next('')
   }

   // ── Copy answers ──────────────────────────────────────────────────────────

   openCopy(r: PeerInputRequest): void {
      this.activeRequest = r
      this.copyGroups = []
      this.loadingDetail = true

      this.http.get<PeerInputDetail>(`${this.base}/api/userreview/peer-input/${r.id}`).subscribe({
         next: (detail) => {
            this.copyGroups = this.buildGroups(detail)
            this.loadingDetail = false
         },
         error: (err) => {
            this.loadingDetail = false
            this.activeRequest = null
            this.toastr.error(this.errorText(err, 'Could not load their answers.'))
         },
      })
   }

   closeCopy(): void {
      this.activeRequest = null
      this.copyGroups = []
   }

   get allItems(): CopyItem[] {
      return this.copyGroups.flatMap((g) => g.items)
   }

   get selectedCount(): number {
      return this.allItems.filter((i) => i.selected).length
   }

   selectAll(selected: boolean): void {
      this.allItems.forEach((i) => (i.selected = selected))
   }

   applyCopy(): void {
      const chosen = this.allItems.filter((i) => i.selected).map((i) => i.response)
      if (!chosen.length) return

      const confirmText =
         chosen.length === 1
            ? 'Replace your answer to this question with theirs?'
            : `Replace your answers to these ${chosen.length} questions with theirs?`
      if (!confirm(confirmText)) return

      this.copyAnswers.emit(chosen)
      this.closeCopy()
   }

   firstName(name: string | null | undefined): string {
      return (name ?? '').trim().split(/\s+/)[0] || 'Peer'
   }

   private buildGroups(detail: PeerInputDetail): CopyGroup[] {
      const responses = (detail.responses ?? []).filter((r) => (r.respondentType ?? 'reviewer') === 'reviewer')
      const byId = new Map(responses.map((r) => [r.questionId, r]))
      const groups: CopyGroup[] = []

      // Use the peer's copy of the extra criteria so ids match what they answered
      const extraCriteria = mapAdditionalResponsibilities(detail.additionalResponsibilities, detail.revieweeUserId, detail.reviewType)

      for (const section of this.template?.sections ?? []) {
         const items: CopyItem[] = []

         if (section.id === 'section_additional_performance_criteria') {
            for (const r of extraCriteria) {
               const questionId = `additional_responsibility_${r.id}`
               const response = byId.get(questionId)
               if (!response) continue
               const mine = readReviewerAnswer(this.userReview, questionId)
               items.push({
                  questionId,
                  label: r.responsibilityText,
                  theirs: formatAnswerText(null, response.responseValue, response.notes, null, PERFORMANCE_OPTIONS),
                  yours: formatAnswerText(null, mine.value, mine.notes, null, PERFORMANCE_OPTIONS),
                  response,
                  selected: false,
               })
            }
         } else {
            for (const q of section.questions ?? []) {
               if (q.type === 'static' || !q.reviewerLabel?.trim()) continue
               const response = byId.get(q.id)
               if (!response) continue
               const mine = readReviewerAnswer(this.userReview, q.id)
               items.push({
                  questionId: q.id,
                  label: plainLabel(q.reviewerLabel),
                  theirs: formatAnswerText(q, response.responseValue, response.notes, response.otherText),
                  yours: formatAnswerText(q, mine.value, mine.notes, mine.otherText),
                  response,
                  selected: false,
               })
            }
         }

         if (items.length) groups.push({ title: section.reviewerTitle || section.revieweeTitle, items })
      }

      return groups
   }

   private errorText(err: any, fallback: string): string {
      const text = typeof err?.error === 'string' ? err.error : err?.error?.message
      return text && text.length < 200 ? text : fallback
   }

   trackById = (_: number, r: PeerInputRequest) => r.id
   trackByQuestion = (_: number, i: CopyItem) => i.questionId
}
